/**
 * The push-permission state machine — PURE, and separated from the browser on
 * purpose.
 *
 * Everything interesting about Web Push permission is precedence between facts
 * that arrive from four different browser APIs, on platforms that disagree
 * about which of them even exist. That logic is worth a direct unit test;
 * `navigator.serviceWorker` is not mockable in a way that would make such a
 * test trustworthy. So the facts are gathered in `readPushEnvironment` (later,
 * `usePushStatus`) and decided here.
 *
 * Authority: ADR-0006 and skill `notifications`.
 */

/**
 * What the customer is told, and what — if anything — they can do about it.
 *
 * ⚠️ PRECEDENCE MATTERS AND IS NOT OBVIOUS. See `derivePushState`.
 */
export type PushState =
  /** iOS Safari, not installed to the home screen. Asking cannot work; instruct instead. */
  | 'needs_install'
  /** Permission was refused. TERMINAL for us — see below. */
  | 'blocked'
  /** The browser has no Web Push at all. Nothing to offer. */
  | 'unsupported'
  /** Push is supported but the service worker is not registered yet. Not the customer's problem. */
  | 'not_ready'
  /** Granted AND subscribed AND the subscription is THIS account's — the only state that actually delivers. */
  | 'on'
  /** Granted but the subscription is gone. The customer believes they are covered and is not. */
  | 'lapsed'
  /**
   * Granted, subscribed — but the subscription belongs to a DIFFERENT account
   * that used this device before. See `derivePushState` step 5.
   */
  | 'foreign'
  /** Never asked. The one state where the browser prompt is still available. */
  | 'off';

/**
 * The browser facts `derivePushState` decides from. Gathered by
 * `readPushEnvironment`; every one of them is a plain value so a test can
 * state a situation exactly.
 */
export interface PushEnvironment {
  /** `'Notification' in window` */
  hasNotificationApi: boolean;
  /** `'PushManager' in window` */
  hasPushManager: boolean;
  /** `'serviceWorker' in navigator` */
  hasServiceWorker: boolean;
  /** A service worker registration actually exists. False until the PWA milestone lands. */
  serviceWorkerRegistered: boolean;
  /** iOS or iPadOS, by user agent. */
  isIos: boolean;
  /** Running as an installed PWA (display-mode: standalone, or iOS `navigator.standalone`). */
  isStandalone: boolean;
  /** `Notification.permission`, or null when the API is absent. */
  permission: NotificationPermission | null;
  /** `registration.pushManager.getSubscription()` returned something. */
  hasSubscription: boolean;
  /**
   * The current subscription's endpoint, or null when there is none.
   *
   * ⚠️ PERSONAL DATA (ADR-0006 / CLAUDE.md): an endpoint identifies a
   * customer's device. It is carried here ONLY so the ownership lookup below
   * has something to key on. Never log it, never put it in an error message,
   * never let it leave the browser except in the `push_subscriptions` write
   * that is its whole purpose.
   */
  endpoint: string | null;
  /**
   * Does the browser's current subscription belong to the ACCOUNT that is
   * logged in right now?
   *
   * ⚠️ Every other field on this interface is a DEVICE fact. This one is not,
   * and that asymmetry is the entire point: `permission` and `hasSubscription`
   * are per-origin-per-device, so on a shared phone they stay true after the
   * previous customer logs out. Deciding from them alone told the next customer
   * "notifications are on" while nothing was addressed to them.
   *
   * `null` means "not determined" — no session, or the lookup failed. Treated
   * as "assume it is theirs" so a transient network failure never accuses a
   * customer of using someone else's device.
   */
  subscriptionIsMine: boolean | null;
}

/**
 * Decide what to show, most-terminal first.
 *
 * The order is the whole design:
 *
 * 1. `needs_install` — iOS Safari outside the installed app has NO Notification
 *    or PushManager at all. Reporting `unsupported` there would be technically
 *    true and completely useless: installing fixes it, and the customer is the
 *    only one who can do that. This must therefore outrank `unsupported`.
 *
 * 2. `blocked` — a refusal is terminal for us regardless of anything else, and
 *    outranks `not_ready` because it stays true after the service worker lands.
 *    ⚠️ There is NO programmatic recovery: `requestPermission()` resolves
 *    'denied' immediately without showing anything, and clearing our own
 *    storage changes nothing — the grant lives in the browser's per-origin
 *    settings, not in our app. The only path is the customer's own browser UI,
 *    so this state shows INSTRUCTIONS, never a button that would silently
 *    no-op.
 *
 * 3. `unsupported` — genuinely no Web Push, and installing would not help.
 *
 * 4. `not_ready` — supported and not refused, but nothing to subscribe against
 *    yet. Distinct from `unsupported` so the copy can be honest instead of
 *    telling a modern Android phone it is incapable.
 *
 * 5. `on` / `lapsed` / `foreign` — all three are `granted`, and what separates
 *    them is whether a subscription exists AND whose it is. `lapsed` is the
 *    state this widget was originally built for: browsers rotate and prune
 *    endpoints silently, and nothing tells the customer.
 *
 *    `foreign` is the shared-device case, and it is NOT cosmetic. A browser
 *    subscription belongs to the DEVICE, not the account: after customer A
 *    subscribes and logs out, `permission` and `hasSubscription` both stay
 *    true, so customer B was told "on" while every notification addressed to
 *    B went nowhere — and worse, A's subscription stayed live, so A's pushes
 *    kept arriving on the phone B was now holding. Reporting `on` there was a
 *    dead end too: `canSelfHeal` did not cover it, so B was never offered any
 *    action and could never become reachable on that device at all.
 *
 * 6. `off` — the only state where the browser prompt is still available, and
 *    therefore the only one that may trigger it. It is a one-shot: spend it on
 *    a deliberate tap, never on page load.
 */
export function derivePushState(env: PushEnvironment): PushState {
  if (env.isIos && !env.isStandalone) {
    return 'needs_install';
  }

  if (env.permission === 'denied') {
    return 'blocked';
  }

  if (!env.hasNotificationApi || !env.hasPushManager || !env.hasServiceWorker) {
    return 'unsupported';
  }

  if (!env.serviceWorkerRegistered) {
    return 'not_ready';
  }

  if (env.permission === 'granted') {
    if (!env.hasSubscription) {
      return 'lapsed';
    }
    // `null` (undetermined) deliberately reads as `on`: a failed lookup must
    // not tell a customer on their own phone that the device belongs to
    // somebody else. Only a definite `false` claims that.
    return env.subscriptionIsMine === false ? 'foreign' : 'on';
  }

  return 'off';
}

/**
 * Whether this state can still open the browser's permission prompt.
 *
 * Exists so the component never renders an action that silently does nothing —
 * the failure mode that teaches a customer the app is broken.
 */
export function canRequestPermission(state: PushState): boolean {
  return state === 'off';
}

/**
 * Whether the app can fix this state by itself, without the customer leaving
 * the app. Both states here are already `granted`, so neither needs a prompt
 * nor a gesture-sensitive API.
 *
 *   `lapsed`  — re-subscribe; there is nothing in the way.
 *   `foreign` — drop the previous account's browser subscription and mint a
 *               fresh one. Covered here deliberately: while it was not, the
 *               customer was shown a state with no action at all and stayed
 *               permanently unreachable on that device.
 */
export function canSelfHeal(state: PushState): boolean {
  return state === 'lapsed' || state === 'foreign';
}

/**
 * Whether the customer is actually reachable by push right now.
 *
 * Deliberately narrow: only `on`. Everything else — including `lapsed`, which
 * looks fine from the outside — means a push would not arrive.
 */
export function isDeliverable(state: PushState): boolean {
  return state === 'on';
}

/**
 * Read the environment out of the current browser.
 *
 * Kept beside the pure function rather than inside a hook so there is exactly
 * one place that touches these globals. Safe to call during SSR or in a bare
 * test runner: every access is guarded.
 */
export async function readPushEnvironment(): Promise<PushEnvironment> {
  const hasWindow = typeof window !== 'undefined';
  const hasNavigator = typeof navigator !== 'undefined';

  if (!hasWindow || !hasNavigator) {
    return {
      hasNotificationApi: false,
      hasPushManager: false,
      hasServiceWorker: false,
      serviceWorkerRegistered: false,
      isIos: false,
      isStandalone: false,
      permission: null,
      hasSubscription: false,
      endpoint: null,
      subscriptionIsMine: null,
    };
  }

  const hasNotificationApi = 'Notification' in window;
  const hasPushManager = 'PushManager' in window;
  const hasServiceWorker = 'serviceWorker' in navigator;

  // iPadOS 13+ reports itself as a Mac; the touch-point check is the standard
  // way to tell an iPad from a desktop Safari that genuinely supports push.
  const ua = navigator.userAgent;
  const isIos =
    /iPad|iPhone|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1);

  const isStandalone =
    (typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone)').matches) ||
    // iOS never implemented display-mode: standalone for this; it has its own flag.
    (navigator as Navigator & { standalone?: boolean }).standalone === true;

  const permission = hasNotificationApi ? Notification.permission : null;

  let serviceWorkerRegistered = false;
  let hasSubscription = false;
  let endpoint: string | null = null;

  if (hasServiceWorker && hasPushManager) {
    try {
      // getRegistration(), NOT ready: `ready` never settles when no worker has
      // been registered, which is exactly the pre-PWA-milestone state today —
      // awaiting it would hang the widget forever instead of showing not_ready.
      const registration = await navigator.serviceWorker.getRegistration();
      serviceWorkerRegistered = registration !== undefined;

      if (registration) {
        const subscription = await registration.pushManager.getSubscription();
        hasSubscription = subscription !== null;
        endpoint = subscription?.endpoint ?? null;
      }
    } catch {
      // A browser that throws here is one we cannot subscribe through; treat it
      // as "no registration" rather than propagating into the render.
      serviceWorkerRegistered = false;
      hasSubscription = false;
      endpoint = null;
    }
  }

  return {
    hasNotificationApi,
    hasPushManager,
    hasServiceWorker,
    serviceWorkerRegistered,
    isIos,
    isStandalone,
    permission,
    hasSubscription,
    endpoint,
    // Not a browser fact — `usePushStatus` fills it in from the database once
    // it knows who is logged in. Undetermined here by construction.
    subscriptionIsMine: null,
  };
}
